import {
    getFirestore,
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    where,
    orderBy,
    limit,
    startAfter,
    addDoc,
    serverTimestamp,
} from "firebase/firestore";
import { firebaseConfig } from "./firebase"
import { initializeApp } from "firebase/app";
import { getLocalizedProductName, getLocalizedProductDescription } from "../utils/multilingualUtils";
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

/**
 * Get products for customer-facing pages (Shop, ItemSection, etc.)
 * @param {string} storeId - The ID of the store
 * @param {object} options - Query options (limit, lastDoc, filters, sorting)
 * @returns {Promise<object>} Object with products array and last document for pagination
 */
export const getUserStoreProducts = async (storeId, options = {}) => {
    try {
        const {
            limit: queryLimit = 20,
            lastDoc = null,
            category = null,
            status = "active",
            sortBy = 'createdAt',
            sortDirection = 'desc'
        } = options;

        // Ensure we have a valid storeId
        if (!storeId) {
            throw new Error("Store ID is required to get products");
        }

        // Create a reference to the products collection for this store
        const productsRef = collection(db, "stores", storeId, "products");

        // Start building the query
        let queryConstraints = [];

        // Apply status filter (default to active for customer searches)
        queryConstraints.push(where("status", "==", status));

        // Apply category filter if specified
        if (category) {
            queryConstraints.push(where("category", "==", category));
        }

        // Add sorting
        queryConstraints.push(orderBy(sortBy, sortDirection));

        // Add pagination
        queryConstraints.push(limit(queryLimit));

        // Add startAfter if we have a lastDoc for pagination
        if (lastDoc) {
            queryConstraints.push(startAfter(lastDoc));
        }

        // Execute the query
        const productsQuery = query(productsRef, ...queryConstraints);
        const querySnapshot = await getDocs(productsQuery);

        // Process results
        const products = [];
        let lastVisible = null;

        querySnapshot.forEach((doc) => {
            products.push({
                id: doc.id,
                ...doc.data()
            });
            lastVisible = doc;
        });

        return {
            products,
            lastDoc: lastVisible,
            hasMore: querySnapshot.docs.length === queryLimit
        };
    } catch (error) {
        // console.error('Error getting user store products:', error);
        throw error;
    }
};

/**
 * Get products for a specific store
 * @param {string} storeId - The ID of the store
 * @param {object} options - Query options (limit, lastDoc, filters)
 * @returns {Promise<object>} Object with products array and last document for pagination
 */
export const getAdminStoreProducts = async (storeId, options = {}) => {
    try {
        const { limit: queryLimit = 20, lastDoc = null, category = null, status = null, sortBy = 'createdAt', sortDirection = 'desc' } = options;

        // Create a reference to the products collection for this store
        const productsRef = collection(db, "stores", storeId, "products");

        // Start building the query
        let queryConstraints = [];

        // Apply filters
        if (category) {
            queryConstraints.push(where("category", "==", category));
        }

        if (status) {
            queryConstraints.push(where("status", "==", status));
        }

        // Add sorting
        queryConstraints.push(orderBy(sortBy, sortDirection));

        // Add pagination
        queryConstraints.push(limit(queryLimit));

        // Add startAfter if we have a lastDoc for pagination
        if (lastDoc) {
            queryConstraints.push(startAfter(lastDoc));
        }

        // Execute the query
        const productsQuery = query(productsRef, ...queryConstraints);
        const querySnapshot = await getDocs(productsQuery);

        // Process results
        const products = [];
        let lastVisible = null;

        querySnapshot.forEach((doc) => {
            products.push({
                id: doc.id,
                ...doc.data()
            });
            lastVisible = doc;
        });

        return {
            products,
            lastDoc: lastVisible
        };
    } catch (error) {
        throw error;
    }
};

/**
 * Search products in a store by name, description, tags, SKU, or barcode
 * @param {string} storeId - The ID of the store
 * @param {string} searchTerm - The search term
 * @param {object} options - Additional options (limit, category filter, etc.)
 * @returns {Promise<array>} Array of matching products
 */
export const searchStoreProducts = async (storeId, searchTerm, options = {}) => {
    try {
        if (!storeId) {
            throw new Error("Store ID is required to search products");
        }

        if (!searchTerm || searchTerm.trim() === "") {
            return [];
        }

        const { category = null, status = "active", limit: queryLimit = 50 } = options;

        // Get all products from the store (we'll filter client-side for better search)
        const productsRef = collection(db, "stores", storeId, "products");
        let queryConstraints = [];

        // Filter by category if specified
        if (category) {
            queryConstraints.push(where("category", "==", category));
        }

        // Filter by status (default to Active for customer searches)
        if (status) {
            queryConstraints.push(where("status", "==", status));
        }

        queryConstraints.push(limit(queryLimit));

        const productsQuery = query(productsRef, ...queryConstraints);
        const querySnapshot = await getDocs(productsQuery);

        const products = [];
        querySnapshot.forEach((doc) => {
            products.push({
                id: doc.id,
                ...doc.data()
            });
        });

        // Client-side filtering for comprehensive search
        const searchTermLower = searchTerm.toLowerCase().trim();
        const searchWords = searchTermLower.split(' ').filter(word => word.length > 0);

        const filteredProducts = products.filter(product => {
            // Get multilingual text for search, fallback to 'en' or first available language
            const productName = getLocalizedProductName(product, 'en') || '';
            const productDescription = getLocalizedProductDescription(product, 'en') || '';

            // Search in product name
            const nameMatch = productName.toLowerCase().includes(searchTermLower);

            // Search in product description
            const descriptionMatch = productDescription.toLowerCase().includes(searchTermLower);

            // Search in SKU
            const skuMatch = product.sku && product.sku.toLowerCase().includes(searchTermLower);

            // Search in barcode
            const barcodeMatch = product.barcode && product.barcode.toLowerCase().includes(searchTermLower);

            // Search in tags (exact match or partial match)
            const tagsMatch = product.tags && product.tags.some(tag =>
                tag.toLowerCase().includes(searchTermLower) ||
                searchWords.some(word => tag.toLowerCase().includes(word))
            );

            // Search in category
            const categoryMatch = product.category && product.category.toLowerCase().includes(searchTermLower);

            return nameMatch || descriptionMatch || skuMatch || barcodeMatch || tagsMatch || categoryMatch;
        });

        // Sort by relevance (prioritize name matches, then description, then tags)
        filteredProducts.sort((a, b) => {
            const aProductName = getLocalizedProductName(a, 'en') || '';
            const bProductName = getLocalizedProductName(b, 'en') || '';
            const aProductDescription = getLocalizedProductDescription(a, 'en') || '';
            const bProductDescription = getLocalizedProductDescription(b, 'en') || '';

            const aNameMatch = aProductName.toLowerCase().includes(searchTermLower);
            const bNameMatch = bProductName.toLowerCase().includes(searchTermLower);

            if (aNameMatch && !bNameMatch) return -1;
            if (!aNameMatch && bNameMatch) return 1;

            const aDescMatch = aProductDescription.toLowerCase().includes(searchTermLower);
            const bDescMatch = bProductDescription.toLowerCase().includes(searchTermLower);

            if (aDescMatch && !bDescMatch) return -1;
            if (!aDescMatch && bDescMatch) return 1;

            return 0;
        });

        return filteredProducts;
    } catch (error) {
        throw error;
    }
};

/**
 * Get popular/suggested search tags from all products in a store
 * @param {string} storeId - The ID of the store
 * @returns {Promise<array>} Array of popular tags
 */
export const getPopularSearchTags = async (storeId) => {
    try {
        if (!storeId) {
            throw new Error("Store ID is required to get popular tags");
        }

        const productsRef = collection(db, "stores", storeId, "products");
        const queryConstraints = [
            where("status", "==", "active"),
            limit(100)
        ];

        const productsQuery = query(productsRef, ...queryConstraints);
        const querySnapshot = await getDocs(productsQuery);

        const tagCounts = {};

        querySnapshot.forEach((doc) => {
            const product = doc.data();
            if (product.tags && Array.isArray(product.tags)) {
                product.tags.forEach(tag => {
                    const normalizedTag = tag.toLowerCase().trim();
                    tagCounts[normalizedTag] = (tagCounts[normalizedTag] || 0) + 1;
                });
            }
        });

        // Sort tags by frequency and return top 5
        const sortedTags = Object.entries(tagCounts)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 5)
            .map(([tag]) => tag);

        return sortedTags;
    } catch (error) {
        throw error;
    }
};

/**
 * Get a single product by ID
 * @param {string} storeId - The ID of the store
 * @param {string} productId - The ID of the product
 * @returns {Promise<object>} Product data
 */
export const getProductById = async (storeId, productId) => {
    try {
        if (!storeId) {
            throw new Error("Store ID is required to get product");
        }

        const productDoc = await getDoc(doc(db, "stores", storeId, "products", productId));

        if (!productDoc.exists()) {
            throw new Error("Product not found");
        }

        return {
            id: productDoc.id,
            ...productDoc.data()
        };
    } catch (error) {
        throw error;
    }
};

/**
 * Create a new product in a store (SECURE - Server-side)
 * @param {string} storeId - The ID of the store
 * @param {object} productData - The product data
 * @returns {Promise<string>} The new product ID
 */
export const createProduct = async (storeId, productData) => {
    try {
        if (!storeId) {
            throw new Error("Store ID is required");
        }

        // Call the secure Cloud Function
        const createProductFunction = httpsCallable(functions, 'createProduct');
        const result = await createProductFunction({
            storeId: storeId,
            productData: productData
        });

        if (result.data.success) {
            return result.data.productId;
        } else {
            throw new Error(result.data.message || 'Failed to create product');
        }
    } catch (error) {
        console.error('Error creating product:', error);
        throw new Error(error.message || 'Failed to create product');
    }
};

/**
 * Update an existing product (SECURE - Server-side)
 * @param {string} storeId - The ID of the store
 * @param {string} productId - The ID of the product
 * @param {object} productData - Updated product data
 * @returns {Promise}
 */
export const updateProduct = async (storeId, productId, productData) => {
    try {
        if (!storeId || !productId) {
            throw new Error("Store ID and Product ID are required");
        }

        // Call the secure Cloud Function
        const updateProductFunction = httpsCallable(functions, 'updateProduct');
        const result = await updateProductFunction({
            storeId: storeId,
            productId: productId,
            productData: productData
        });

        if (!result.data.success) {
            throw new Error(result.data.message || 'Failed to update product');
        }

        return result.data;
    } catch (error) {
        console.error('Error updating product:', error);
        throw new Error(error.message || 'Failed to update product');
    }
};

/**
 * Delete a product (SECURE - Server-side)
 * @param {string} storeId - The ID of the store
 * @param {string} productId - The ID of the product
 * @returns {Promise}
 */
export const deleteProduct = async (storeId, productId) => {
    try {
        if (!storeId || !productId) {
            throw new Error("Store ID and Product ID are required");
        }

        // Call the secure Cloud Function
        const deleteProductFunction = httpsCallable(functions, 'deleteProduct');
        const result = await deleteProductFunction({
            storeId: storeId,
            productId: productId
        });

        if (!result.data.success) {
            throw new Error(result.data.message || 'Failed to delete product');
        }

        return result.data;
    } catch (error) {
        console.error('Error deleting product:', error);
        throw new Error(error.message || 'Failed to delete product');
    }
};
//     try {
//         const {
//             limit: queryLimit = 20,
//             lastDoc = null,
//             category = null,
//             status = "active"
//         } = options;

//         // Ensure we have a valid storeId
//         if (!storeId) {
//             throw new Error("Store ID is required to get products");
//         }

//         // Create a reference to the products collection for this store
//         const productsRef = collection(db, "stores", storeId, "products");

//         // Start building the query - always order by createdAt desc for recent items
//         let queryConstraints = [
//             where("status", "==", status),
//         ];

//         // Apply category filter if specified
//         if (category) {
//             queryConstraints.push(where("category", "==", category));
//         }

//         // Always sort by creation date (newest first) for consistent results
//         queryConstraints.push(orderBy("createdAt", "desc"));

//         // Add pagination
//         queryConstraints.push(limit(queryLimit));

//         // Add startAfter if we have a lastDoc for pagination
//         if (lastDoc) {
//             queryConstraints.push(startAfter(lastDoc));
//         }

//         // Execute the query
//         const productsQuery = query(productsRef, ...queryConstraints);
//         const querySnapshot = await getDocs(productsQuery);

//         // Process results
//         const products = [];
//         let lastVisible = null;

//         querySnapshot.forEach((doc) => {
//             products.push({
//                 id: doc.id,
//                 ...doc.data()
//             });
//             lastVisible = doc;
//         });

//         return {
//             products,
//             lastDoc: lastVisible,
//             hasMore: querySnapshot.docs.length === queryLimit
//         };
//     } catch (error) {
//         throw error;
//     }
// };